import React, { useEffect, useState } from 'react';
import { Box, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { RootState } from '../../../store';
import { fetchByDimension } from '../../../store/callStats/actionCreators';
import {
  QualityDimension,
  mosBand,
  scoreBand,
} from '../../../store/callStats/callStatsTypes';
import ScoreCard from '../components/ScoreCard';
import ChartPanel from '../components/ChartPanel';
import { CHART_COLORS } from '../components/scoreColors';

const DIMENSIONS: { value: QualityDimension; label: string }[] = [
  { value: 'user', label: 'By user' },
  { value: 'duration', label: 'By duration' },
  { value: 'participants', label: 'By participants' },
];

const QualitySection: React.FC = () => {
  const dispatch = useDispatch();
  const { scores, byDimension, filters } = useSelector(
    (state: RootState) => state.callStats,
  );
  const [dimension, setDimension] = useState<QualityDimension>('user');
  const sc = scores.data;
  // Backend COALESCEs an unscored window to all-zeros; render neutral "—"
  // cards rather than a wall of red "Poor".
  const unscored = (sc?.callsScored ?? 0) === 0;

  useEffect(() => {
    dispatch(fetchByDimension(filters, dimension) as never);
  }, [dispatch, filters, dimension]);

  return (
    <Box>
      <Box display="flex" gap={2} flexWrap="wrap">
        {[
          <ScoreCard
            key="exp"
            label="Experience Score"
            score={sc?.experience ?? 0}
            band={scoreBand(sc?.experience ?? 0)}
            unscored={unscored}
            highlight
          />,
          <ScoreCard
            key="vid"
            label="Video Score"
            score={sc?.video ?? 0}
            band={scoreBand(sc?.video ?? 0)}
            unscored={unscored}
          />,
          <ScoreCard
            key="aud"
            label="Audio Score"
            score={sc?.audio ?? 0}
            band={scoreBand(sc?.audio ?? 0)}
            unscored={unscored}
          />,
          <ScoreCard
            key="mos"
            label="MOS Score"
            score={sc?.mos ?? 0}
            max={5}
            band={mosBand(sc?.mos ?? 0)}
            display={sc?.mos != null ? sc.mos.toFixed(2) : '—'}
            unscored={unscored}
          />,
          <ScoreCard
            key="con"
            label="Connectivity Score"
            score={sc?.connectivity ?? 0}
            band={scoreBand(sc?.connectivity ?? 0)}
            unscored={unscored}
          />,
        ].map((card, i) => (
          <Box key={i} sx={{ flex: '1 1 150px', minWidth: 150 }}>
            {card}
          </Box>
        ))}
      </Box>

      <Box mt={2}>
        <ChartPanel
          title="Aggregated quality"
          subtitle={`Mean scores segmented ${DIMENSIONS.find((d) => d.value === dimension)?.label.toLowerCase()} · ${sc?.callsScored ?? 0} calls scored`}
          loading={byDimension.loading}
          error={byDimension.error}
          empty={
            !byDimension.loading &&
            (byDimension.data?.buckets.length ?? 0) === 0
          }
          mocked={byDimension.mocked}
          minHeight={300}
          action={
            <ToggleButtonGroup
              value={dimension}
              exclusive
              size="small"
              color="secondary"
              onChange={(_, v) => v && setDimension(v)}
            >
              {DIMENSIONS.map((d) => (
                <ToggleButton key={d.value} value={d.value} sx={{ px: 1.5 }}>
                  {d.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          }
        >
          <ResponsiveContainer width="100%" height={300}>
            <BarChart
              data={byDimension.data?.buckets ?? []}
              margin={{ top: 8, right: 12, left: -12, bottom: 0 }}
            >
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis
                dataKey="label"
                tick={{ fontSize: 11 }}
                interval={0}
                angle={-12}
                textAnchor="end"
                height={56}
              />
              <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} />
              <Tooltip />
              <Legend />
              <Bar
                dataKey="experience"
                name="Experience"
                fill={CHART_COLORS[4]}
                radius={[4, 4, 0, 0]}
              />
              <Bar
                dataKey="audio"
                name="Audio"
                fill={CHART_COLORS[1]}
                radius={[4, 4, 0, 0]}
              />
              <Bar
                dataKey="video"
                name="Video"
                fill={CHART_COLORS[0]}
                radius={[4, 4, 0, 0]}
              />
              <Bar
                dataKey="connectivity"
                name="Connectivity"
                fill={CHART_COLORS[2]}
                radius={[4, 4, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </ChartPanel>
      </Box>
    </Box>
  );
};

export default QualitySection;
